import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalItems, setTotalItems] = useState(null);
  const [error, setError] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  const fetchCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCartItems([]);
      setTotalPrice(0);
      setTotalItems(null);
      setError('Please log in to view your cart.');
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = response.data.items || [];
      setCartItems(items);
      const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      setTotalPrice(total);
      setTotalItems(itemCount || null);
      setError(null);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setError('Failed to load cart. Please try again.');
    }
  };

  useEffect(() => {
    fetchCart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Intentional: run once on mount to fetch cart data
  const addToCart = async (product, weight = '500g', quantity = 1) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to add items to cart.');
      return false;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/cart/add`,
        {
          productId: String(product.id),
          name: product.name,
          image: product.image,
          weight,
          price: product.price,
          quantity,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
      setError(null);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      setError('Failed to add item to cart.');
      return false;
    }
  };

  const updateQuantity = async (productId, weight, quantity) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to update cart.');
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/cart`,
        { productId: String(productId), weight, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCart();
      setError(null);
    } catch (error) {
      console.error('Error updating cart:', error);
      setError('Failed to update cart.');
    }
  };

  const removeFromCart = async (productId, weight) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to remove items.');
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/cart/remove`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { productId: String(productId), weight },
      });
      await fetchCart();
      setError(null);
    } catch (error) {
      console.error('Error removing from cart:', error);
      setError('Failed to remove item.');
    }
  };

  const clearCart = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to clear cart.');
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/cart/remove`, {
        headers: { Authorization: `Bearer ${token}` },
        data: {},
      });
      setCartItems([]);
      setTotalPrice(0);
      setTotalItems(null);
      setError(null);
    } catch (error) {
      console.error('Error clearing cart:', error);
      setError('Failed to clear cart.');
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        totalPrice,
        totalItems,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart,
        error,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);